import type { ForgeDoc } from '@atlassian/forge-ui-types';
import type { AppearanceTypes } from '@atlaskit/flag/types';

import { type HistoryApiProvider } from './methods/history';
import type { PermissionBridgeMethods } from './methods/permissions/usePermission';

type UserConsentState = {
	requiresUserConsent: boolean;
};

type BaseOpenRovoPayload = {
	prompt?: string;
	sourceAppId?: string;
	sourceEnvironmentId?: string;
	sourceInstallationId?: string;
};
type ForgeAgentPayload = BaseOpenRovoPayload & { agentKey: string; agentName: string };
type NonForgeAgentPayload = BaseOpenRovoPayload & { agentKey?: never; agentName?: string };
export type OpenRovoPayload = ForgeAgentPayload | NonForgeAgentPayload;

export interface BridgeConfig extends HistoryApiProvider, PermissionBridgeMethods {
	changeWindowTitle?: (title: string) => void;
	close?: (payload?: any) => boolean | undefined | void;
	closeFlag?: (options: Pick<FlagOptions, 'id'>) => boolean | undefined;
	emitReadyEvent?: () => void;
	onClose?: (payload?: any) => boolean | undefined | void;
	onError?: (options: { error: Error }) => boolean | undefined | void;
	open?: () => boolean | undefined | void;
	openModal?: (opts: any, consentState?: UserConsentState) => boolean | undefined;
	openRovo?: (payload: OpenRovoPayload) => Promise<boolean | void>;
	reconcile?: (options: { forgeDoc: ForgeDoc }) => boolean | undefined | void;
	refresh?: () => boolean | undefined;
	showFlag?: (options: FlagOptions) => boolean | undefined;
	spaPush?: ((path: string, state?: any) => void) | ((url: string, forceReload?: boolean) => void);
	submit?: (payload?: any) => boolean | undefined;
}

export type FlagAppearance = Exclude<AppearanceTypes, 'normal'>;
export type FlagType = FlagAppearance;

export interface Flag {
	close: () => Promise<boolean | void>;
}

export interface FlagAction {
	onClick: () => void;
	text: string;
}

export interface FlagOptions {
	actions?: FlagAction[];
	/**
	 * When `appearance` is given, the Flag is bold. If not, it's a normal Flag.
	 */
	appearance?: FlagAppearance;
	description?: string;
	id?: number | string;
	isAutoDismiss?: boolean;
	onClose?: () => void;
	title?: string;
	/**
	 * `type` is used to determine the icon of Flag.
	 * If `appearance` is given, `type` is overriden to equal `appearance`.
	 */
	type?: FlagType;
}

export interface ModalOptions {
	context?: any;
	icon?: string;
	onClose?: (payload: any) => any;
	resource?: string | null;
	size?: string;
	title?: string;
}

export enum FeatureFlagEnv {
	development = 'development',
	staging = 'staging',
	production = 'production',
}

export enum FeatureFlagEventTypes {
	INITIALIZATION = 'initialization',
	CHECKFLAG = 'checkFlag',
}
